import { useEffect, useRef, useState } from 'react';

// Speculation-rules prerendering (docs/KB.md gotcha 24). A KB page's Tickets link prerenders
// the portal on hover: this whole app boots, fetches and renders in a hidden document the
// visitor may never see (the prerender can simply be discarded). Plain reads are fine — they
// are the point — but nothing may have a SIDE EFFECT before the page is actually shown: no
// beacon, no socket, no service-worker registration/update, no "mark read", no genuine
// ticket view, no redirect. Those wait for activation (`prerenderingchange`).
//
// Outside a prerender every helper here is synchronous and free, so the normal path keeps
// its exact timing.

export const isPrerendering = () => typeof document !== 'undefined' && document.prerendering === true;

let activated = null;
// Resolves at once (a resolved promise) unless this document is a prerender — then on
// activation. A prerender that's discarded never resolves.
export function whenActivated() {
  if (!isPrerendering()) return Promise.resolve();
  if (!activated) {
    activated = new Promise((resolve) => {
      document.addEventListener('prerenderingchange', () => resolve(), { once: true });
    });
  }
  return activated;
}

// Run `fn` now — synchronously — or once the prerender is activated. Returns a cancel for an
// effect's cleanup (a no-op when it already ran).
export function onActivated(fn) {
  if (!isPrerendering()) { fn(); return () => {}; }
  let live = true;
  whenActivated().then(() => { if (live) fn(); });
  return () => { live = false; };
}

// true once the document is being shown (always true outside a prerender) — for mounting a
// component whose mere mount has side effects (UpdatePrompt registers the service worker).
export function useActivated() {
  const [shown, setShown] = useState(() => !isPrerendering());
  useEffect(() => (shown ? undefined : onActivated(() => setShown(true))), [shown]);
  return shown;
}

// Header-hover prerender (gotcha 24): a brief hover (HOVER_MS pointerenter) or keyboard focus
// anywhere in the header asks the browser to prerender where the header leads — once per
// document — by inserting a `<script type="speculationrules">` with an `immediate` list rule.
// The rule texts are byte-constant (shared/src/kb/hoverRules.js) and the CSP allows exactly
// those by sha256 (server/src/lib/security-headers.js). The per-link `moderate` rule of the
// Speculation-Rules header stays (it covers a tap: pointerdown). Pointers that can't hover
// (touch) are skipped; browsers without speculation rules (or with preloading switched off —
// Brave/Helium by default, uBlock's "Disable pre-fetching") ignore the element, and the local
// list cache (gotcha 25) is what makes those fast. Chrome allows 10 immediate prerenders per
// page; this asks for one or two.
const HOVER_MS = 100;
let speculated = false;
export function insertSpeculationRules(text) {
  if (speculated || !text) return;
  speculated = true;
  const s = document.createElement('script');
  s.type = 'speculationrules';
  s.textContent = text; // exact bytes — the CSP hash is of this string
  document.head.append(s);
}
// `getRules(header)` runs at trigger time and returns the rules text (null = nothing to do).
export function useHoverPrerender(ref, getRules) {
  const rules = useRef(getRules);
  rules.current = getRules;
  useEffect(() => {
    const el = ref.current;
    if (!el || speculated || !window.matchMedia?.('(hover: hover)').matches) return undefined;
    let timer = null;
    const go = () => { timer = null; insertSpeculationRules(rules.current(el)); };
    const enter = (e) => { if (e.pointerType !== 'touch' && !timer) timer = setTimeout(go, HOVER_MS); };
    const leave = () => { clearTimeout(timer); timer = null; };
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('focusin', go);
    return () => { leave(); el.removeEventListener('pointerenter', enter); el.removeEventListener('pointerleave', leave); el.removeEventListener('focusin', go); };
  }, [ref]);
}
